import qs from 'node:querystring'

import environment from '@/environments/environment.dev'

import HttpClient from './apiHttpClient'

const apiClient = new HttpClient(environment.shopFAPIUrl)
const ORDER_ENDPOINT = '/v1.0/order'

export interface Order {
  salesOrderNumber: string
  status: string
  orderType: string
  referenceNumber: string
  currencyUnit: string
  additionalCost: number
  cartSubTotal: number
  cartTotal: number
  cartTotalVAT: number
  shippingCosts: number
  vat: number
  orderDate: string
  invoicesNumbers: string[]
  showInvoice?: boolean
  items: Item[]
  deliveryDates: DeliveryDate[]
  shipments: string[]
  checkoutSettings: CheckoutSettings
}

export interface OrderListItem {
  salesOrderNumber: string
  status: string
  orderType: string
  referenceNumber: string
  currencyUnit: string
  totalPrice: number
  orderDate: string
  invoicesNumbers: string[]
  showInvoice?: boolean
  additionalPrice: number
  countryId: string
  customerId: string
  shippingCosts: number
  voucher: {
    name: string
    voucherCode: string
    discount: number
  }
}

export interface GetOrdersResponse {
  orderList: {
    items: OrderListItem[]
    totalCount: number
    pageSize: number
    totalPages: number
    currentPage: number
    hasNext: boolean
    hasPrevious: boolean
  }
  statusList: string[]
}

export interface GetOrdersParams {
  pageNumber?: number
  pageSize?: number
  searchTerm?: string
  sessionId?: string
  sortBy?: string
  orderBy?: string
  status?: string
  dateFrom?: string
  dateTo?: string
}

export interface Item {
  materialNumber: string
  quantity: number
  hasMinQuantity: boolean
  itemCateg: string
  positionId: number
  name: string
  imgUrl: string
  price: number
  freightPrice: number
  recyclingFee: number
  status: string
  discount: number
  subTotal: number
  total: number
  isMachine: boolean
  isDangerous: boolean
  isAvailable: boolean
  serialNumber: string
  reqQuantity: number
  requiresInstruction: boolean
  displayListPrice: boolean
  displayInNavigation: boolean
  displayAddToCart: boolean
  disableBProgramCheckbox: boolean
  tax: number
  longDescription: string
  categoryId: number
}

interface DeliveryDate {
  quantity: number
  materialNumber: string
  deliveryDate: string
  itemCateg: string
}

interface CheckoutSettings {
  shippingNotes: boolean
  includingVAT: boolean
  b_Program_Ordering: boolean
  headLvlConditions: string
  positionLvlConditions: string
  deliveryBlockBProgram: string
  deliveryBlockForNewAddress: string
  bProgramOrderType: string
  currencyUnit: string
}

export const orderService = {
  /**
   * Get paginated list of orders with filtering and sorting
   * @param params - Query parameters for orders list
   * @returns Promise resolving to orders list with pagination info
   */
  async getOrders(params: GetOrdersParams): Promise<GetOrdersResponse | undefined> {
    const {
      pageNumber = 1,
      pageSize = 10,
      searchTerm,
      sessionId,
      sortBy = 'desc',
      orderBy = 'orderDate',
      status = '',
      dateFrom,
      dateTo,
    } = params

    const url = `${ORDER_ENDPOINT}/list`

    try {
      const { data } = await apiClient.get<{ orderList: GetOrdersResponse }>(url, {
        params: {
          pageNumber,
          pageSize,
          orderBy,
          sortBy,
          searchTerm,
          status,
          sessionId,
          dateFrom,
          dateTo,
        },
      })

      return data?.orderList
    } catch (error) {
      console.error('error:', error?.message)
      throw error
    }
  },

  async getOrder({ orderNumber }) {
    try {
      const url = `${ORDER_ENDPOINT}/${orderNumber}`
      const { data } = await apiClient.get<{ data: Order }>(url)

      return data
    } catch (error) {
      console.error('error:', error?.message)
      throw new Error(error?.message)
    }
  },

  async placeOrder({ orderData, sessionId }) {
    try {
      const url = `${ORDER_ENDPOINT}/create`

      const { data } = await apiClient.post(url, {
        body: orderData,
        params: {
          sessionId,
        },
      })

      return data
    } catch (error) {
      console.error('error place order:', error?.message)
      throw new Error(error?.message)
    }
  },

  async getShoppingCart({ sessionId }) {
    try {
      const url = `${ORDER_ENDPOINT}/simulate`

      const response = await apiClient.get(url, {
        params: { sessionId },
      })
      return response
    } catch (error) {
      console.error('error get shopping cart:', error?.message)
      throw new Error(error?.message)
    }
  },

  async getPriceBoxData({ materialNumber }) {
    try {
      const url = `${ORDER_ENDPOINT}/simulate/${materialNumber}`
      const { data } = await apiClient.get(url)

      return data
    } catch (error) {
      console.error('error get price box data:', error?.message)
      throw new Error(error?.message)
    }
  },

  async getMultipleInvoicesByInvoiceNumbers({ invoiceNumbers }: { invoiceNumbers: string[] }) {
    try {
      const url = `${ORDER_ENDPOINT}/invoice`
      const { data } = await apiClient.get(url, {
        params: {
          invoiceNumbers,
        },
        paramsSerializer: {
          serialize: (params) => qs.stringify(params),
        },
      })

      return data
    } catch (error) {
      console.error('error get multiple invoices:', error?.message)
      throw new Error(error?.message)
    }
  },

  async getProductsListByOrderNumber(orderNumber) {
    try {
      const url = `${ORDER_ENDPOINT}/${orderNumber}/invoice`
      const { data } = await apiClient.get(url)

      return data
    } catch (error) {
      console.error('error get products list by order number:', error?.message)
      throw new Error(error?.message)
    }
  },

  async validateZipCode(country, zipCode) {
    try {
      const { data } = await apiClient.put(`${ORDER_ENDPOINT}/zip-code/validate`, {
        body: {
          country,
          zipCode,
        },
      })
      return data
    } catch (error) {
      console.error('error validate zip code:', error?.message)
      throw new Error(error?.message)
    }
  },
}
